<template>
  <div class="compliance-report">
    <!-- Header Section -->
    <div class="header-section">
      <div class="container">
        <h1 class="main-title">
          <i class="fas fa-stopwatch"></i>
          Location Owner Responsiveness
        </h1>
        <p class="subtitle">Identify the current owner per location and whether they are responsive (all active policies expire after today)</p>
        
        <!-- Filters -->
        <div class="filters-section">
          <div class="filter-group" v-if="userProject">
            <label>Your Project:</label>
            <div class="project-display">
              <i class="fas fa-folder"></i>
              Project {{ userProject.project_id }}
            </div>
          </div>
          <div class="filter-group">
            <label for="vendor-filter">Vendor or Store Search:</label>
            <input 
              id="vendor-filter"
              v-model="filters.vendorName" 
              type="text" 
              placeholder="Search vendor name"
              @input="debouncedFetch"
            />
          </div>
          <div class="filter-group">
            <label for="responsive-filter">Responsive Filter:</label>
            <select id="responsive-filter" v-model="filters.responsiveFilter" @change="debouncedFetch">
              <option value="all">All</option>
              <option value="responsive">Responsive only</option>
              <option value="nonresponsive">Non-responsive only</option>
            </select>
          </div>
          <div class="filter-group">
            <label>&nbsp;</label>
            <div class="checkboxes">
              <label class="inline">
                <input type="checkbox" v-model="filters.includePrevious" @change="debouncedFetch" />
                Include previous owners
              </label>
              <label class="inline">
                <input type="checkbox" v-model="filters.monopolisticOnly" @change="debouncedFetch" />
                Monopolistic states: ignore WC
              </label>
              <label class="inline">
                <input type="checkbox" v-model="filters.texasMode" @change="debouncedFetch" />
                Texas: ignore WC
              </label>
            </div>
          </div>
          <button @click="refreshData" class="refresh-btn" :disabled="loading">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
            Refresh
          </button>
          <button @click="exportToCSV" class="refresh-btn" :disabled="loading || !vendors || vendors.length === 0" style="background:#3b82f6;">
            <i class="fas fa-file-export"></i>
            Export
          </button>
        </div>
        
        <!-- Statistics Cards -->
        <div class="stats-cards" v-if="statistics">
          <div class="stat-card">
            <div class="stat-value">{{ statistics.total_stores }}</div>
            <div class="stat-label">Total Locations</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ statistics.responsive_stores }}</div>
            <div class="stat-label">Responsive</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ statistics.non_responsive_stores }}</div>
            <div class="stat-label">Non-Responsive</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-section">
      <div class="container">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Loading compliance data...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ error }}</p>
          <button @click="fetchData" class="retry-btn">Try Again</button>
        </div>

        <!-- Vendors Table -->
        <div v-else-if="vendors.length > 0" class="vendors-table-container">
          <h2>Current Owner per Location</h2>
          <div class="table-wrapper">
            <table class="vendors-table">
              <thead>
                <tr>
                  <th>Store Base</th>
                  <th>Current Owner</th>
                  <th>Responsive</th>
                  <th>Missing/Expired</th>
                  <th>Total Policies</th>
                  <th>Compliance Rate</th>
                  <th>Last Updated</th>
                  <th>Non-Compliant Policies</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(vendor, index) in validVendors" :key="'vendor-' + index">
                  <tr>
                    <td class="vendor-name">{{ vendor.store_base || 'N/A' }}</td>
                    <td>{{ vendor.vendor_name || 'N/A' }}</td>
                    <td>
                      <span class="status-badge" :class="getResponsiveBadgeClass(vendor.responsive)">
                        {{ vendor.responsive ? 'Yes' : 'No' }}
                      </span>
                    </td>
                    <td>{{ vendor.missing_or_expired_count || 0 }}</td>
                    <td>{{ vendor.total_policies || 0 }}</td>
                    <td>
                      <span class="compliance-rate" :class="getComplianceClass(vendor.compliance_rate)">
                        {{ vendor.compliance_rate || 0 }}%
                      </span>
                    </td>
                    <td>{{ formatDate(vendor.last_updated) }}</td>
                    <td>
                      <span v-if="vendor.non_compliant_policies && vendor.non_compliant_policies.length">
                        {{ vendor.non_compliant_policies.join(', ') }}
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td>
                      <button 
                        @click="togglePolicyDetails(vendor.verification_id)"
                        class="toggle-details-btn"
                      >
                        <i class="fas" :class="showDetails[vendor.verification_id] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        Details
                      </button>
                    </td>
                  </tr>
                  <!-- Policy Details Row -->
                  <tr v-show="showDetails[vendor.verification_id]" class="policy-details-row">
                  <td colspan="8">
                    <div class="policy-details">
                      <h4>Policies for {{ vendor?.vendor_name || 'Unknown Vendor' }}</h4>
                      <div v-if="vendor?.policies && vendor.policies.length > 0" class="policies-grid">
                        <div v-for="policy in vendor.policies" :key="policy?.policy_id || Math.random()" class="policy-card">
                          <div class="policy-header">
                            <span class="policy-number">{{ policy?.policy_name || 'N/A' }}</span>
                            <span class="policy-type">{{ policy?.policy_status_group || 'Unknown' }}</span>
                            <span class="acceptance-status" :class="{ accepted: policy?.policy_status_group === 'complete', rejected: policy?.policy_status_group !== 'complete' }">
                              {{ policy?.policy_status_group === 'complete' ? 'COMPLIANT' : 'NOT COMPLIANT' }}
                            </span>
                          </div>
                          <div class="policy-details-grid">
                            <div><strong>Agent:</strong> {{ policy?.agent_email || 'N/A' }}</div>
                            <div><strong>Status:</strong> {{ policy?.policy_status_group || 'Unknown' }}</div>
                            <div><strong>Project:</strong> {{ policy?.project_title || 'N/A' }}</div>
                            <div><strong>Expires:</strong> {{ formatDate(policy?.expiration) }}</div>
                          </div>
                        </div>
                      </div>
                      <p v-else class="no-policies">No policies found for this vendor.</p>

                      <div v-if="filters.includePrevious && vendor.previous_verifications && vendor.previous_verifications.length" style="margin-top: 1.5rem;">
                        <h4>Previous Owners</h4>
                        <div class="prev-owners-list">
                          <div class="prev-owner" v-for="prev in vendor.previous_verifications" :key="'prev-' + prev.verification_id">
                            <div class="prev-owner-row">
                              <div class="prev-owner-name"><strong>{{ prev.vendor_name }}</strong> <span v-if="prev.store_suffix">({{ prev.store_suffix }})</span></div>
                              <div class="prev-owner-policies">Policies: {{ prev.accepted_policies || 0 }}/{{ prev.total_policies || 0 }} ({{ computeComplianceRate(prev) }}%)</div>
                              <div class="prev-owner-updated">Updated: {{ formatDate(prev.last_updated) }}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <i class="fas fa-search"></i>
          <h3>No compliance data found</h3>
          <p>Try adjusting your filters or check your database connection.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { debounce } from 'lodash'
import api from '@/services/api'
import { saveAs } from 'file-saver'

export default {
  name: 'ComplianceResponsivenessReport',
  setup() {
    const loading = ref(false)
    const error = ref(null)
    const vendors = ref([])
    const statistics = ref(null)
    const userProject = ref(null)
    const showDetails = reactive({})
    
    // Computed property to filter out undefined vendors
    const validVendors = computed(() => {
      if (!vendors.value || !Array.isArray(vendors.value)) {
        return []
      }
      return vendors.value.filter(vendor => vendor && vendor.verification_id)
    })
    
    const filters = reactive({
      vendorName: '',
      status: '',
      responsiveFilter: 'all',
      includePrevious: false,
      monopolisticOnly: false,
      texasMode: false
    })

    const fetchData = async () => {
      loading.value = true
      error.value = null
      
      try {
        const params = new URLSearchParams()
        if (filters.vendorName) params.append('vendor_name', filters.vendorName)
        params.append('include_previous', filters.includePrevious ? 'true' : 'false')
        if (filters.monopolisticOnly) params.append('monopolistic_only', 'true')
        if (filters.texasMode) params.append('texas_mode', 'true')

        // New endpoint for responsiveness & multi-location
        const response = await api.get(`/compliance/responsiveness-report/?${params}`)

        const result = response.data

        if (result.success) {
          const groups = (result.data && result.data.groups) ? result.data.groups : []
          // Flatten current_verification into vendors list for table display
          let flattened = groups.map(group => {
            const cv = group.current_verification || {}
            const total = cv.total_policies || 0
            const accepted = cv.accepted_policies || 0
            const complianceRate = total > 0 ? Math.round((accepted / total) * 10000) / 100 : 0
            const computedStatus = complianceRate === 100 ? 'Compliant' : 'Do Not Pay'
            return {
              ...cv,
              store_base: group.store_base || null,
              compliance_rate: complianceRate,
              computed_status: computedStatus,
              previous_verifications: group.previous_verifications || []
            }
          })

          if (filters.responsiveFilter === 'responsive') {
            flattened = flattened.filter(v => v.responsive === true)
          } else if (filters.responsiveFilter === 'nonresponsive') {
            flattened = flattened.filter(v => v.responsive === false)
          }

          vendors.value = flattened

          // Build simple statistics summary
          const summary = (result.data && result.data.summary) ? result.data.summary : null
          statistics.value = summary ? {
            total_stores: summary.total_stores,
            responsive_stores: summary.responsive_stores,
            non_responsive_stores: summary.non_responsive_stores
          } : null

          userProject.value = result.data.user_project
        } else {
          error.value = result.error || 'Failed to fetch compliance data'
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          error.value = 'Authentication failed. Please log in again.'
        } else {
          error.value = 'Network error: ' + (err.message || 'Unknown error')
        }
      } finally {
        loading.value = false
      }
    }

    const debouncedFetch = debounce(fetchData, 500)
    
    const refreshData = () => {
      fetchData()
    }

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return ''
      const str = String(value)
      if (str.includes('"') || str.includes(',') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"'
      }
      return str
    }

    const exportToCSV = () => {
      const headers = [
        'Store Base',
        'Owner Type',
        'Vendor Name',
        'State',
        'Responsive',
        'Missing/Expired',
        'Total Policies',
        'Accepted Policies',
        'Compliance Rate %',
        'Last Updated',
        'Non-Compliant Policies',
        'WC Policies Filtered'
      ]

      const rows = []
      const list = Array.isArray(vendors.value) ? vendors.value : []

      list.forEach(v => {
        rows.push([
          v.store_base || '',
          'Current',
          v.vendor_name || '',
          v.detected_state || '',
          v.responsive === true ? 'Yes' : (v.responsive === false ? 'No' : ''),
          v.missing_or_expired_count ?? '',
          v.total_policies ?? '',
          v.accepted_policies ?? '',
          v.compliance_rate ?? '',
          v.last_updated ? new Date(v.last_updated).toISOString() : '',
          Array.isArray(v.non_compliant_policies) ? v.non_compliant_policies.join('; ') : '',
          v.wc_policies_filtered ?? ''
        ])

        if (filters.includePrevious && Array.isArray(v.previous_verifications)) {
          v.previous_verifications.forEach(prev => {
            rows.push([
              v.store_base || '',
              'Previous',
              prev.vendor_name || '',
              '',
              '',
              '',
              prev.total_policies ?? '',
              prev.accepted_policies ?? '',
              computeComplianceRate(prev) ?? '',
              prev.last_updated ? new Date(prev.last_updated).toISOString() : '',
              '',
              ''
            ])
          })
        }
      })

      const csv = [headers, ...rows]
        .map(row => row.map(escapeCsv).join(','))
        .join('\n')

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19)
      saveAs(blob, `responsiveness-report-${ts}.csv`)
    }

    const togglePolicyDetails = (verificationId) => {
      showDetails[verificationId] = !showDetails[verificationId]
    }

    const getStatusClass = (status) => {
      const statusMap = {
        'compliant': 'status-success',
        'do not pay': 'status-danger',
        'completed': 'status-success',
        'pending': 'status-warning',
        'expired': 'status-danger',
        'active': 'status-success'
      }
      return statusMap[status?.toLowerCase()] || 'status-neutral'
    }

    const getComplianceClass = (rate) => {
      if (rate >= 90) return 'compliance-excellent'
      if (rate >= 70) return 'compliance-good'
      if (rate >= 50) return 'compliance-warning'
      return 'compliance-poor'
    }

    const getResponsiveBadgeClass = (responsive) => {
      return responsive ? 'status-success' : 'status-danger'
    }

    const computeComplianceRate = (item) => {
      const total = item?.total_policies || 0
      const accepted = item?.accepted_policies || 0
      return total > 0 ? Math.round((accepted / total) * 10000) / 100 : 0
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    // Fetch data automatically on mount for authenticated user
    onMounted(() => {
      fetchData()
    })

    return {
      loading,
      error,
      vendors,
      validVendors,
      statistics,
      userProject,
      filters,
      showDetails,
      fetchData,
      debouncedFetch,
      refreshData,
      exportToCSV,
      togglePolicyDetails,
      getStatusClass,
      computeComplianceRate,
      getResponsiveBadgeClass,
      getComplianceClass,
      formatDate
    }
  }
}
</script>

<style scoped>
/* Reuse styles from LiveComplianceReport for consistency */
.compliance-report {
  min-height: 100vh;
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
}

/* Header Section */
.header-section {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

.main-title {
  color: white;
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  text-align: center;
  font-weight: 700;
}

.main-title i {
  margin-right: 1rem;
  color: #10b981;
}

.subtitle {
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  font-size: 1.2rem;
  margin-bottom: 2rem;
}

/* Filters */
.filters-section {
  display: flex;
  gap: 1rem;
  align-items: end;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
}

.filter-group input,
.filter-group select {
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  min-width: 200px;
}

.filter-group input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.filter-group select option {
  background: #1f2937;
  color: white;
}

.project-display {
  padding: 0.75rem;
  background: rgba(16, 185, 129, 0.2);
  border: 1px solid rgba(16, 185, 129, 0.5);
  border-radius: 8px;
  color: #10b981;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 200px;
}

.project-display i {
  font-size: 1.1rem;
}

.refresh-btn {
  padding: 0.75rem 1.5rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  background: #059669;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Statistics Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #10b981;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
}

/* Compliance rate colors */
.compliance-excellent .stat-value { color: #10b981; }
.compliance-good .stat-value { color: #3b82f6; }
.compliance-warning .stat-value { color: #f59e0b; }
.compliance-poor .stat-value { color: #ef4444; }

/* Content Section */
.content-section {
  padding: 3rem 0;
}

.loading-state, .error-state, .empty-state {
  text-align: center;
  padding: 3rem;
  color: white;
}

.loading-state i, .error-state i, .empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.6);
}

.retry-btn {
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

/* Table Styles */
.vendors-table-container {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.vendors-table-container h2 {
  margin-bottom: 1.5rem;
  color: #1f2937;
}

.table-wrapper {
  overflow-x: auto;
}

.vendors-table {
  width: 100%;
  border-collapse: collapse;
}

.vendors-table th,
.vendors-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.vendors-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.vendor-name {
  font-weight: 600;
  color: #1f2937;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-success { background: #d1fae5; color: #065f46; }
.status-warning { background: #fef3c7; color: #92400e; }
.status-danger { background: #fee2e2; color: #991b1b; }
.status-neutral { background: #f3f4f6; color: #374151; }

.compliance-rate {
  font-weight: 600;
}

.toggle-details-btn {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
}

.toggle-details-btn:hover {
  background: #2563eb;
}

.policy-details-row {
  background: #f9fafb;
}

.policy-details {
  padding: 1.5rem;
}

.policy-details h4 {
  margin-bottom: 1rem;
  color: #1f2937;
}

.policies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.policy-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
}

.policy-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.policy-number {
  font-weight: 600;
  color: #1f2937;
}

.policy-type {
  background: #e5e7eb;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.acceptance-status {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.acceptance-status.accepted {
  background: #d1fae5;
  color: #065f46;
}

.acceptance-status.rejected {
  background: #fee2e2;
  color: #991b1b;
}

.policy-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
  font-size: 0.875rem;
}

.no-policies {
  color: #6b7280;
  font-style: italic;
}

@media (max-width: 768px) {
  .main-title {
    font-size: 2rem;
  }
  
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-group input {
    min-width: auto;
  }
  
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .policies-grid {
    grid-template-columns: 1fr;
  }
}
</style>
